import { create } from 'zustand'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// Generic Supabase session gate. RequireAuth + LoginPage consume this. The
// source project layered an approval-status / profile check on top; that's
// app-specific and was dropped from the baseline. Add it back in a separate
// store (or extend this one) if your app needs gated access.

interface AuthState {
  user: User | null
  session: Session | null
  passwordRecovery: boolean
  loading: boolean
  initialized: boolean
  error: string | null

  initialize: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<boolean>
  requestPasswordReset: (email: string) => Promise<boolean>
  updatePassword: (password: string) => Promise<boolean>
  signOut: () => Promise<void>
  clearError: () => void
}

function isPasswordRecoveryRedirect(): boolean {
  if (typeof window === 'undefined') return false

  const hashParams = new URLSearchParams(window.location.hash.slice(1))
  const searchParams = new URLSearchParams(window.location.search)
  return hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery'
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  session: null,
  // Capture this synchronously, before Supabase consumes the URL fragment.
  passwordRecovery: isPasswordRecoveryRedirect(),
  loading: false,
  initialized: false,
  error: null,

  initialize: async () => {
    // Idempotent: only the first call wires up the session + listener.
    if (get().initialized) return

    // Mark initialization as claimed before the first await so React Strict
    // Mode cannot register a duplicate auth listener.
    set({ loading: true, initialized: true })

    try {
      // Register before reading the session so a recovery event emitted during
      // URL-session detection cannot be missed.
      supabase.auth.onAuthStateChange((event, session) => {
        set((state) => ({
          session,
          user: session?.user ?? null,
          passwordRecovery:
            event === 'PASSWORD_RECOVERY'
              ? true
              : event === 'SIGNED_OUT'
                ? false
                : state.passwordRecovery,
        }))
      })

      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) throw error

      set({
        session,
        user: session?.user ?? null,
        loading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize auth',
        loading: false,
        initialized: true,
      })
    }
  },

  signInWithEmail: async (email: string, password: string) => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        loading: false,
      })
    }
  },

  signUpWithEmail: async (email: string, password: string): Promise<boolean> => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      })
      if (error) throw error
      set({ loading: false })
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign up',
        loading: false,
      })
      return false
    }
  },

  requestPasswordReset: async (email: string): Promise<boolean> => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })
      if (error) throw error
      set({ loading: false })
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to send reset email',
        loading: false,
      })
      return false
    }
  },

  updatePassword: async (password: string): Promise<boolean> => {
    set({ loading: true, error: null })

    try {
      if (!get().session) {
        throw new Error('This password reset link has expired. Request a new one and try again.')
      }

      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error

      // End the recovery session locally so the new password is proven with a
      // fresh sign-in. No password or recovery state is persisted by this store.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' })
      if (signOutError) throw signOutError

      set({
        user: null,
        session: null,
        passwordRecovery: false,
        loading: false,
      })
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update password',
        loading: false,
      })
      return false
    }
  },

  signOut: async () => {
    set({ loading: true, error: null })

    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      set({ user: null, session: null, passwordRecovery: false, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to sign out',
        loading: false,
      })
    }
  },

  clearError: () => set({ error: null }),
}))
